import orderBy from 'lodash/orderBy'
import _filter from 'lodash/filter'
import type { Dispatch, SetStateAction } from 'react'
import React, { useCallback, useMemo } from 'react'
import sift from 'sift'
import type { ListActionResult, OnFilterFunction, Search } from '../types/QueryInput'
import { useDeepCompareEffect } from '../useDeepCompareEffect'
import { useListAction } from '../useListAction'
import type { Sort } from '@ztechapps/common.contract'

export type UseLocalListActionResult<T = any> = ListActionResult<T> & {
  list: T[]
  setList: Dispatch<SetStateAction<T[]>>
}

type UseLocalListActionOptions<T = any> = {
  defaultSort?: Sort<T>
  defaultSearch?: Search
}

/**
 * Another variation of useListAction, that works on local/in-memory list
 * It manipulates the local list, including the callback for updating the list
 * If we are sure that sift works with react-native environment, we can move this to umbrella.uni-core
 * @returns {*}
 */
export function useLocalListAction<T = any>(
  list: T[],
  options: UseLocalListActionOptions = {
    defaultSort: null,
    defaultSearch: null,
  }
): UseLocalListActionResult<T> {
  const [localList, setList] = React.useState<T[]>(list)

  //if there are changes in list (by content), update localList
  useDeepCompareEffect(() => {
    setList(list)
  }, [list])

  //ref so onFilter can read the latest `list` without re-creating when caller
  //passes a new array reference with identical content (common with Apollo).
  const listRef = React.useRef(list)
  listRef.current = list

  const listActionResult = useListAction({
    defaultSort: options.defaultSort,
    defaultSearch: options.defaultSearch,
  })

  const onFilter: OnFilterFunction = useCallback(
    ({ template, searchObject, searchValue }) => {
      const onFilterResult = listActionResult.onFilter({ searchObject, searchValue, template })
      const newList = _filter(listRef.current, sift(onFilterResult.filter)) as T[]
      setList(newList)
      return onFilterResult
    },
    [listActionResult, setList]
  )

  const onSort = useCallback(
    ({ sortValue, sortObject }) => {
      const sortResult = listActionResult.onSort({
        sortValue,
        sortObject,
      })

      //we will do local sort here
      const sortedList = orderBy<T>(localList, [sortValue.name], [sortValue.direction === 1 ? 'asc' : 'desc'])

      setList(sortedList)

      return sortResult
    },
    [localList, listActionResult, setList]
  )

  return useMemo(
    () => ({
      ...listActionResult,
      onFilter,
      onSort,
      list: localList,
      setList,
    }),
    [listActionResult, onFilter, onSort, localList, setList]
  )
}
